/**
 * String utility functions for the application
 */

/**
 * Capitalizes the first letter of each word in a string
 * @param {string} text - The text to capitalize
 * @returns {string} - The capitalized text
 * 
 * @example
 * capitalizeWords("john doe") // "John Doe"
 * capitalizeWords("MARY JANE SMITH") // "Mary Jane Smith"
 * capitalizeWords("alice-in-wonderland") // "Alice-in-wonderland"
 * capitalizeWords("") // ""
 * capitalizeWords(null) // ""
 */
export const capitalizeWords = (text) => {
  if (!text || typeof text !== 'string') return '';

  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Capitalizes the first letter of a string
 * @param {string} text - The text to capitalize
 * @returns {string} - The capitalized text
 * 
 * @example
 * capitalizeFirst("hello world") // "Hello world"
 * capitalizeFirst("JOHN") // "John"
 */
export const capitalizeFirst = (text) => {
  if (!text || typeof text !== 'string') return '';

  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/**
 * Converts a string to title case (capitalizes first letter of each word)
 * @param {string} text - The text to convert
 * @returns {string} - The title case text
 * 
 * @example
 * toTitleCase("hello world") // "Hello World"
 * toTitleCase("JOHN DOE") // "John Doe"
 */
export const toTitleCase = (text) => {
  if (!text || typeof text !== 'string') return '';

  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Truncates a string to a specified length and adds ellipsis
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} - The truncated text
 * 
 * @example
 * truncateText("This is a very long text", 10) // "This is a..."
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || typeof text !== 'string') return '';
  if (text.length <= maxLength) return text;

  return text.substring(0, maxLength).trim() + '...';
};

/**
 * Removes extra whitespace and normalizes spaces
 * @param {string} text - The text to normalize
 * @returns {string} - The normalized text
 * 
 * @example
 * normalizeWhitespace("  hello    world  ") // "hello world"
 */
export const normalizeWhitespace = (text) => {
  if (!text || typeof text !== 'string') return '';

  return text.replace(/\s+/g, ' ').trim();
};

/**
 * Formats a name by capitalizing words and normalizing whitespace
 * @param {string} name - The name to format
 * @returns {string} - The formatted name
 * 
 * @example
 * formatName("  john    doe  ") // "John Doe"
 * formatName("MARY JANE SMITH") // "Mary Jane Smith"
 */
export const formatName = (name) => {
  if (!name || typeof name !== 'string') return '';

  return capitalizeWords(normalizeWhitespace(name));
};

/**
 * Formats a date to a readable format: "2 Jan, 2025, 07:19 PM"
 * @param {Date|string|number} date - The date to format
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeTime - Whether to include time (default: true)
 * @param {boolean} options.includeSeconds - Whether to include seconds (default: false)
 * @param {string} options.timeZone - Time zone (default: 'local')
 * @returns {string} - The formatted date string
 * 
 * @example
 * formatDate(new Date()) // "2 Jan, 2025, 07:19 PM"
 * formatDate("2025-01-02T19:19:00Z") // "2 Jan, 2025, 07:19 PM"
 * formatDate(new Date(), { includeTime: false }) // "2 Jan, 2025"
 * formatDate(new Date(), { includeSeconds: true }) // "2 Jan, 2025, 07:19:30 PM"
 */
export const formatDate = (date, options = {}) => {
  const {
    includeTime = true,
    includeSeconds = false,
    timeZone = 'local'
  } = options;

  if (!date) return '';

  try {
    const dateObj = new Date(date);

    if (isNaN(dateObj.getTime())) {
      return '';
    }

    // Month abbreviations
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Get date components
    const day = dateObj.getDate();
    const month = monthNames[dateObj.getMonth()];
    const year = dateObj.getFullYear();

    // Format date as "2 Jan 2025"
    let formattedDate = `${day} ${month} ${year}`;

    // Add time if needed
    if (includeTime) {
      const timeOptions = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      };

      if (includeSeconds) {
        timeOptions.second = '2-digit';
      }

      const timeStr = dateObj.toLocaleTimeString('en-US', timeOptions);
      formattedDate += `, ${timeStr}`;
    }

    return formattedDate;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Formats a date to a short format: "10/10/2025"
 * @param {Date|string|number} date - The date to format
 * @returns {string} - The formatted date string
 * 
 * @example
 * formatDateShort(new Date()) // "10/10/2025"
 */
export const formatDateShort = (date) => {
  if (!date) return '';

  try {
    const dateObj = new Date(date);

    if (isNaN(dateObj.getTime())) {
      return '';
    }

    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Formats a date to a long format: "October 10, 2025"
 * @param {Date|string|number} date - The date to format
 * @returns {string} - The formatted date string
 * 
 * @example
 * formatDateLong(new Date()) // "October 10, 2025"
 */
export const formatDateLong = (date) => {
  if (!date) return '';

  try {
    const dateObj = new Date(date);

    if (isNaN(dateObj.getTime())) {
      return '';
    }

    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Formats a date to a relative format: "2 hours ago", "3 days ago", etc.
 * @param {Date|string|number} date - The date to format
 * @returns {string} - The formatted relative date string
 * 
 * @example
 * formatDateRelative(new Date(Date.now() - 2 * 60 * 60 * 1000)) // "2 hours ago"
 */
export const formatDateRelative = (date) => {
  if (!date) return '';

  try {
    const dateObj = new Date(date);

    if (isNaN(dateObj.getTime())) {
      return '';
    }

    const now = new Date();
    const diffInSeconds = Math.floor((now - dateObj) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} month${diffInMonths === 1 ? '' : 's'} ago`;
    }

    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears} year${diffInYears === 1 ? '' : 's'} ago`;
  } catch (error) {
    console.error('Error formatting relative date:', error);
    return '';
  }
};

// Format field names for display
export const formatFieldName = (fieldName) => {
  if (!fieldName) return '';

  // Special cases mapping for fields
  const fieldMappings = {
    'company_size': 'Company Size',
    'companySize': 'Company Size',
    'leadValue': 'Lead Value',
    'scoreLabel': 'Score Label',
    'annualRevenue': 'Annual Revenue',
    'employeeCount': 'Employee Count'
  };

  // Check if we have a special mapping
  if (fieldMappings[fieldName]) {
    return fieldMappings[fieldName];
  }

  // Convert camelCase/snake_case to Title Case
  return fieldName
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\b\w/g, char => char.toUpperCase()) // Capitalize first letter of each word
    .trim();
};

// Format operator names for display
export const formatOperatorName = (operatorName) => {
  if (!operatorName) return '';

  // Special cases mapping for operators
  const operatorMappings = {
    'equals': 'Equals',
    'not_equals': 'Not Equals',
    'greater_than': 'Greater Than',
    'less_than': 'Less Than',
    'greater_than_equal': 'Greater Than or Equal',
    'less_than_equal': 'Less Than or Equal',
    'contains': 'Contains',
    'in': 'In',
    'not_in': 'Not In'
  };

  // Check if we have a special mapping
  if (operatorMappings[operatorName]) {
    return operatorMappings[operatorName];
  }

  // Convert snake_case to Title Case as fallback
  return operatorName
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\b\w/g, char => char.toUpperCase()) // Capitalize first letter of each word
    .trim();
};

// Format condition values for display
export const formatConditionValue = (field, value) => {
  if (!value) return '';

  // Special handling for source field values
  if (field === 'source') {
    if (typeof value === 'string') {
      return value
        .split(/[_-]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
  }

  // For other fields or unmapped values, return as is
  return value;
};

/**
 * Formats a number as Indian currency (INR)
 * @param {number|string} amount - The amount to format
 * @param {Object} options - Formatting options
 * @param {boolean} options.showSymbol - Whether to show currency symbol (default: true)
 * @param {number} options.minimumFractionDigits - Minimum fraction digits (default: 0)
 * @param {number} options.maximumFractionDigits - Maximum fraction digits (default: 2)
 * @returns {string} - The formatted currency string
 * 
 * @example
 * formatCurrency(1234567) // "₹12,34,567"
 * formatCurrency(1234567.89) // "₹12,34,567.89"
 * formatCurrency(1234567, { showSymbol: false }) // "12,34,567"
 * formatCurrency(0) // "₹0"
 * formatCurrency(null) // "₹0"
 */
export const formatCurrency = (amount, options = {}) => {
  const {
    showSymbol = true,
    minimumFractionDigits = 0,
    maximumFractionDigits = 2
  } = options;

  // Handle null, undefined, or empty values
  if (amount === null || amount === undefined || amount === '') {
    return showSymbol ? '₹0' : '0';
  }

  // Convert to number
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  // Check if valid number
  if (isNaN(numAmount) || !isFinite(numAmount)) {
    return showSymbol ? '₹0' : '0';
  }

  // Format using Indian number system (en-IN locale)
  const formatted = new Intl.NumberFormat('en-IN', {
    style: showSymbol ? 'currency' : 'decimal',
    currency: 'INR',
    minimumFractionDigits,
    maximumFractionDigits
  }).format(numAmount);

  return formatted;
};

/**
 * Capitalizes the first letter of each sentence in a string
 * @param {string} text - The text to capitalize
 * @returns {string} - The text with first letter of each sentence capitalized
 * 
 * @example
 * capitalizeSentences("hello world. how are you? i'm fine!") // "Hello world. How are you? I'm fine!"
 * capitalizeSentences("first sentence. second sentence.") // "First sentence. Second sentence."
 */
export const capitalizeSentences = (text) => {
  if (!text || typeof text !== 'string') return '';

  // Capitalize first letter of the entire text
  let result = text.charAt(0).toUpperCase() + text.slice(1);

  // Find sentence boundaries: . ! or ? followed by space or newline, then a letter
  // Replace the lowercase letter after sentence endings with uppercase
  result = result.replace(/([.!?]\s+)([a-z])/g, (match, punctuation, letter) => {
    return punctuation + letter.toUpperCase();
  });

  // ...existing code...
  return result;
};

/**
 * Formats time string (HH:MM or HH:MM:SS) to AM/PM format
 * @param {string} timeStr - Time string to format
 * @returns {string} - Formatted time in AM/PM format
 */
export const formatTime = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') return 'N/A';

  if (timeStr === 'N/A') return 'N/A';

  try {
    const [hours, minutes] = timeStr.split(':');
    let h = parseInt(hours, 10);
    const m = parseInt(minutes, 10);

    if (isNaN(h) || isNaN(m)) return timeStr;

    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    h = h ? h : 12; // the hour '0' should be '12'

    return `${h}:${m.toString().padStart(2, '0')} ${ampm}`;
  } catch (e) {
    return timeStr;
  }
};


// Default export for convenience
export default {
  capitalizeWords,
  capitalizeFirst,
  toTitleCase,
  truncateText,
  normalizeWhitespace,
  formatName,
  formatDate,
  formatDateShort,
  formatDateLong,
  formatDateRelative,
  formatCurrency,
  capitalizeSentences,
  formatFieldName,
  formatOperatorName,
  formatConditionValue,
  formatTime
};
